import React from 'react';
import { Box, Grid, Typography, useTheme, Tooltip, Badge } from '@mui/material';
import FieldDetails from './FieldDetails';
import CustomCard from '../../../components/@extended/CustomCard';
import { SKIPPED_FIELDS_KEY } from '../../../util/constants';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { checkFieldVisibility } from '../../dynamicFormEngine/utils/dependsOnValidationService';

const FieldList = (props) => {
  const { fieldArr, parentKey, answers, userSiteTrialRole, queriesByFieldKey } = props;
  const theme = useTheme();

  // Helper function to get queries for a field
  const getFieldQueries = (fieldKey) => {
    return queriesByFieldKey?.[fieldKey] || [];
  };

  // Helper function to get open queries count for a field
  const getOpenQueriesCount = (fieldKey) => {
    const fieldQueries = getFieldQueries(fieldKey);
    return fieldQueries.filter((q) => q.status === 'OPEN' || q.status === 'REOPENED').length;
  };

  // Helper function to check if field has any queries
  const fieldHasQueries = (fieldKey) => {
    return getFieldQueries(fieldKey).length > 0;
  };
  const INVALID_FIELDS = ['DATA_HTML', 'FILE_UPLOAD', 'BUTTON'];
  const VALID_FIELDS = [
    'YEAR_PICKER',
    'INLINE_TEXT_FIELD',
    'MULTISELECT_DROP_DOWN',
    'DROP_DOWN',
    'RADIO_BUTTONS',
    'VIDEO_LINK',
    'DOCUMENT_LINK',
    'CHECK_BOX_GROUP',
    'CHECK_BOX',
    'SLIDER',
    'COLOUR_SLIDER',
    'PHONE_INPUT',
    'LABEL',
    'FILE_UPLOAD',
    'SIGNATURE',
    'DATA_LABEL',
  ];

  // Helper to extract the dynamic field group index (e.g., "fieldName-2" returns 2, non-dynamic returns null)
  const getDynamicGroupIndex = (fieldKey) => {
    if (!fieldKey) return null;
    const match = fieldKey.match(/-(\d+)$/);
    if (!match) return null;
    const index = parseInt(match[1], 10);
    return !isNaN(index) && typeof index === 'number' ? index : null;
  };

  // Check if transitioning from main fields to first custom group
  const isTransitionToCustomGroup = (currentField, nextField) => {
    const currentIndex = getDynamicGroupIndex(currentField?.key);
    const nextIndex = getDynamicGroupIndex(nextField?.key);

    // Current field is main (non-dynamic), next field is custom (dynamic)
    return currentIndex === null && nextIndex !== null;
  };

  // Check if the next field belongs to a different dynamic group
  const isLastFieldInGroup = (currentField, nextField) => {
    const currentIndex = getDynamicGroupIndex(currentField?.key);
    const nextIndex = getDynamicGroupIndex(nextField?.key);

    // Only add separator if current field is dynamic (has index)
    if (currentIndex === null) return false;

    // Add separator if next field is non-dynamic or has a different index
    return nextIndex === null || currentIndex !== nextIndex;
  };
    
  return (
    <>
      {answers &&
        fieldArr &&
        fieldArr.map((field, index) => {
          const isValid =
            VALID_FIELDS.includes(field.type) &&
            !SKIPPED_FIELDS_KEY.includes(field.key) &&
            checkFieldVisibility(field, answers) &&
            (field.type === 'SIGNATURE' || !field.access || (field.access && field.access == userSiteTrialRole));

          if (!isValid) return null;

          // Find the previous valid field for transition check
          let prevValidField = null;
          for (let i = index - 1; i >= 0; i--) {
            const pField = fieldArr[i];
            const pIsValid =
              VALID_FIELDS.includes(pField.type) &&
              !SKIPPED_FIELDS_KEY.includes(pField.key) &&
              checkFieldVisibility(pField, answers) &&
              (pField.type === 'SIGNATURE' || !pField.access || (pField.access && pField.access == userSiteTrialRole));
            if (pIsValid) {
              prevValidField = pField;
              break;
            }
          }

          // Find the next valid field (skip fields in SKIPPED_FIELDS_KEY)
          let nextField = null;
          for (let i = index + 1; i < fieldArr.length; i++) {
            const nField = fieldArr[i];
            if (!nField) break;
            // Skip fields that are in SKIPPED_FIELDS_KEY
            if (SKIPPED_FIELDS_KEY.includes(nField.key)) continue;
            // Check if field is valid for display
            const nIsValid =
              VALID_FIELDS.includes(nField.type) &&
              checkFieldVisibility(nField, answers) &&
              (nField.type === 'SIGNATURE' || !nField.access || (nField.access && nField.access == userSiteTrialRole));
            if (nIsValid) {
              nextField = nField;
              break;
            }
          }

          const showGroupSeparator = isLastFieldInGroup(field, nextField);
          const showTransitionSeparator = isTransitionToCustomGroup(prevValidField, field);

          const openQueriesCount = getOpenQueriesCount(field.key);
          const hasQueries = fieldHasQueries(field.key);

          return (
            <React.Fragment key={`field${index}`}>
              {/* Separator between main fields and first custom group */}
              {showTransitionSeparator && <Grid item xs={12} id={`transition-separator-${field.key}`} key={`transition-separator-${index}`} sx={{ padding: '0 !important', margin: 0 }} ><Box sx={{ height: 10 }} /></Grid>}
              <Grid
                item
                id={`field-${field.key}`}
                md={(field.textFontWeight || field.tableRow) ? (field.md ?? 6) : 6}
                lg={(field.textFontWeight || field.tableRow) ? (field.lg ?? 6) : 6}
                sm={6}
                xs={12}
                sx={{ 
                  borderBottom: '1px solid #E7E7E7', 
                  paddingY: '16px !important',
                  position: 'relative',
                  // Highlight fields with open queries
                  backgroundColor: openQueriesCount > 0 ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                }}
              >
                <FieldDetails
                  {...props}
                  key={field.label}
                  allAnswers={answers[parentKey] ? answers[parentKey] : answers}
                  signDate={
                    answers[parentKey] ? answers[parentKey][field.currentDateKey] : answers[field.currentDateKey]
                  }
                  details={field}
                  answer={answers[parentKey] ? answers[parentKey][field.key] : answers[field.key]}
                  fieldQueries={getFieldQueries(field.key)}
                  hasOpenQueries={openQueriesCount > 0}
                />
                {field.fields && (
                  <Grid container spacing={2} sx={{ marginTop: 1 }}>
                    {field.fields.map(
                      (childField, childIndex) => {
                        const childOpenQueriesCount = getOpenQueriesCount(childField.key);
                        const childHasQueries = fieldHasQueries(childField.key);
                        
                        return VALID_FIELDS.includes(childField.type) &&
                        !SKIPPED_FIELDS_KEY.includes(childField.key) &&
                        checkFieldVisibility(childField, answers) &&
                        (childField.type === 'SIGNATURE' || !childField.access || (childField.access && childField.access == userSiteTrialRole)) && (
                          <Grid
                            key={parentKey ? `${parentKey}.${childIndex}.${index}` : `aa${childIndex}${index}`}
                            item
                            md={12}
                            sm={12}
                            xs={12}
                            sx={{
                              position: 'relative',
                              backgroundColor: childOpenQueriesCount > 0 ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                              borderRadius: 1,
                              padding: childHasQueries ? '8px' : undefined,
                            }}
                          >
                            <FieldDetails
                              {...props}
                              key={childField.label}
                              allAnswers={answers[parentKey] ? answers[parentKey] : answers}
                              signDate={
                                answers[parentKey]
                                  ? answers[parentKey][childField.currentDateKey]
                                  : answers[childField.currentDateKey]
                              }
                              details={childField}
                              answer={answers[parentKey] ? answers[parentKey][childField.key] : answers[childField.key]}
                              fieldQueries={getFieldQueries(childField.key)}
                              hasOpenQueries={childOpenQueriesCount > 0}
                            />
                          </Grid>
                        )
                      }
                    )}
                  </Grid>
                )}
              </Grid>
              {/* Blank row separator after each dynamic field group */}
              {showGroupSeparator && (
                <Grid item xs={12} id={`group-separator-${field.key}`} key={`separator-${index}`} sx={{ padding: '0 !important', margin: 0 }}>
                  <Box sx={{ height: 10 }} />
                </Grid>
              )}
            </React.Fragment>
          );
        })}
    </>
  );
};
export default FieldList;
